import type jsPDF from 'jspdf'

import { PDF_CONFIG } from './resumeExportPdfConfig'
import { WORK_EXPERIENCE_TEAL_HEADER_BULLET } from './workExperienceRoleHeaderPreview'

export type TealInlineDrawPart = { kind: string; text: string }

export type TealInlineDrawStyle = {
  lineHeightMm: number
  fontSizePt: (kind: string) => number
  fontWeight: (kind: string) => 'bold' | 'normal'
}

export interface TealInlinePdfLayout {
  leftX: () => number
  maxX: number
  getY: () => number
  setY: (y: number) => void
  checkPageBreak: (lineH: number) => void
  onBeforeLine?: () => void
}

/** Flowing Teal header segments with • separators and horizontal wrap. */
export function drawTealInlinePartsOnPdf(
  pdf: jsPDF,
  layout: TealInlinePdfLayout,
  parts: ReadonlyArray<TealInlineDrawPart>,
  style: TealInlineDrawStyle,
  textColor: [number, number, number]
): void {
  if (!parts.length) return

  const { lineHeightMm: lineH, fontSizePt, fontWeight } = style
  const bullet = WORK_EXPERIENCE_TEAL_HEADER_BULLET
  const bulletKind = 'text'

  const applyFont = (kind: string): void => {
    pdf.setFont('helvetica', fontWeight(kind))
    pdf.setFontSize(fontSizePt(kind))
  }

  const measure = (text: string, kind: string): number => {
    applyFont(kind)
    return pdf.getTextWidth(text)
  }

  const drawRun = (text: string, kind: string, x: number, y: number): number => {
    applyFont(kind)
    pdf.text(text, x, y)
    return x + pdf.getTextWidth(text)
  }

  pdf.setTextColor(textColor[0], textColor[1], textColor[2])

  layout.checkPageBreak(lineH)
  layout.onBeforeLine?.()

  let x = layout.leftX()
  let y = layout.getY()
  const left = layout.leftX

  const advanceLine = (): void => {
    y += lineH
    layout.setY(y)
    layout.checkPageBreak(lineH)
    layout.onBeforeLine?.()
    y = layout.getY()
    x = left()
  }

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!
    if (i > 0) {
      const bulletW = measure(bullet, bulletKind)
      if (x + bulletW > layout.maxX && x > left()) {
        advanceLine()
      }
      x = drawRun(bullet, bulletKind, x, y)
    }

    const partW = measure(part.text, part.kind)
    if (x + partW > layout.maxX && x > left()) {
      advanceLine()
    }
    x = drawRun(part.text, part.kind, x, y)
  }

  layout.setY(y + lineH)
}

export function tealLine1DrawStyle(ptToMm: (pt: number) => number): TealInlineDrawStyle {
  return {
    lineHeightMm:
      Math.max(
        ptToMm(PDF_CONFIG.fontSize.companyLineCompany),
        ptToMm(PDF_CONFIG.fontSize.companyLineMeta)
      ) * PDF_CONFIG.lineHeight.body,
    fontSizePt: (kind) =>
      kind === 'company'
        ? PDF_CONFIG.fontSize.companyLineCompany
        : PDF_CONFIG.fontSize.companyLineMeta,
    fontWeight: () => 'bold',
  }
}

/** Classic single-column PDF: position line uses jobTitle pt for the role title. */
export function tealLine2DrawStyleClassic(ptToMm: (pt: number) => number): TealInlineDrawStyle {
  return {
    lineHeightMm: ptToMm(PDF_CONFIG.fontSize.jobTitle) * PDF_CONFIG.lineHeight.jobTitle,
    fontSizePt: (kind) =>
      kind === 'position' ? PDF_CONFIG.fontSize.jobTitle : PDF_CONFIG.fontSize.body,
    fontWeight: (kind) => (kind === 'position' ? 'bold' : 'normal'),
  }
}

/** Two-column PDF: position and meta share body pt. */
export function tealLine2DrawStyleTwoColumn(ptToMm: (pt: number) => number): TealInlineDrawStyle {
  return {
    lineHeightMm: ptToMm(PDF_CONFIG.fontSize.body) * PDF_CONFIG.lineHeight.body,
    fontSizePt: () => PDF_CONFIG.fontSize.body,
    fontWeight: (kind) => (kind === 'position' ? 'bold' : 'normal'),
  }
}
